import * as medal from "@/assets/lotties/medal.json";
import { CommonLocale } from "@/locales/localeid";
import { GLErrorCode } from "@/models/error.model";
import { ExposureStatus } from "@/store/lesson/state";
import { StudyMode } from "@/store/teaching/interfaces";
import { MIN_ZOOM_RATIO } from "@/utils/constant";
import {
  RealtimeHandlerMap,
  REALTIME_EVENT,
  SessionAction,
  WhiteboardAction,
  createRealtimeHandlerKey,
  TeacherAction,
  HelperAction,
  TeamAction,
  TeacherUpdateStudentsBadgeEventData,
  StudentPaletteEventData,
  StudentAction,
  StudentLeaveClassEventData,
  TeacherClearRaisingHandEventData,
  OneToOneRealtimeEventData,
  ToggleTargetEventData,
  TeacherUpdateCanvasObjectEventData,
  ToggleHelperVideoEventData,
  ToggleIndependentModeEventData,
  LessonActionEventData,
  ToggleTeamModeEventData,
  TeacherUpdateMediaStateEventData,
  TeacherUpdateExposureEventData,
  TeacherEndExposureEventData,
  TeacherUpdateAnnotationModeEventData,
  TeacherScrollPdfEventData,
  TeacherZoomSlideEventData,
  TeacherMoveZoomedSlideEventData,
  HelperEnterClassEventData,
  StudentEnterClassEventData,
} from "@/ws";
import { Modal } from "ant-design-vue";
import { reactive } from "vue";
import { fmtMsg } from "vue-glcommonui";
import { ActionContext } from "vuex";
import { ClassActionFromValue, StudentRoomState } from "./state";
import { RemoteTeachingService, ToggleStudentMediaDevicesAsync } from "@/services";
import { Logger } from "@/utils/logger";
import { isTeacherUseOnlySlideVisible } from "@/utils/utils";
import { vuexName, MODULES } from "@/store/utils";
import { calculatePlayedTime } from "@/utils/convertDuration";
import { mapObjectsFromBE } from "@/store/annotation-v2/actions";

export const useStudentRealtimeHandler = ({ commit, dispatch, rootGetters, state }: ActionContext<StudentRoomState, any>): RealtimeHandlerMap => {
  const handlerMap = new Map<string, (data: any, sender: string) => void | Promise<void>>();
  const myStudentId = rootGetters[vuexName(MODULES.STUDENT_ROOM.GETTERS.GET_LOCAL_STUDENT_ID)];

  const timestamps = {
    onTeacherUpdateExposureTimestamp: 0,
  };

  // #region STUDENT MODULE HANDLERS

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.STUDENT, StudentAction.ENTER_CLASS), async (data: StudentEnterClassEventData) => {
    // do nothing
    const { id, isRaisingHand, isPalette } = data;
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_RAISING_HAND, false), {
      studentId: id,
      raisingHand: isRaisingHand,
    });
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_PALETTE, false), {
      studentId: id,
      isPaletteEnabled: isPalette,
    });
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.STUDENT, StudentAction.LEAVE_SESSION),
    async (data: StudentLeaveClassEventData, sender: string) => {
      const studentId = data?.studentId || sender;
      if (!studentId) return;
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.CLEAR_CIRCLE_STATUS, false), { id: studentId });
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.STUDENT, StudentAction.UPDATE_INDEPENDENT_EXPOSURE_ITEM), async () => {
    // do nothing
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.STUDENT, StudentAction.STUDENT_MOVED_APP_TO_BACKGROUND), async () => {
    // do nothing
  });

  // #endregion

  // #region TEACHER MODULE HANDLERS

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.ENTER_CLASS), async (_: any) => {
    // do nothing
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.UPDATE_STUDENT_BADGE),
    async (data: TeacherUpdateStudentsBadgeEventData) => {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENTS_BADGE, false), data);
      if (myStudentId && data.some((item) => item.studentId == myStudentId)) {
        const icon = reactive({ animationData: medal.default });
        await dispatch(
          vuexName(MODULES.APP.DISPATCHES.SET_TOAST, false),
          {
            message: "",
            isPlayingSound: true,
            bigIcon: icon,
            isMedal: true,
          },
          { root: true },
        );
      }
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.UPDATE_STUDENT_PALETTE), async (data: StudentPaletteEventData) => {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_PALETTE, false), data);
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.TOGGLE_STUDENT_MEDIA_DEVICES),
    async (data: ToggleStudentMediaDevicesAsync) => {
      await dispatch(vuexName(MODULES.STUDENT_ROOM.DISPATCHES.HANDLE_TEACHER_TOGGLE_STUDENT_MEDIA_DEVICES, false), data);
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.SESSION, SessionAction.UPDATE_LESSON_AND_UNIT), async () => {
    commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), 1, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), 0, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), undefined, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.CLEAR_LESSON_DATA), null, { root: true });
    await dispatch(vuexName(MODULES.STUDENT_ROOM.DISPATCHES.GET_CLASS_ROOM_INFO, false));
    commit(
      vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_OBJECTS),
      {
        objects: mapObjectsFromBE([]),
      },
      { root: true },
    );
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SET_ONE_ONE), async (data: OneToOneRealtimeEventData) => {
    const student = state.student;
    if (!student) return;

    commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_INITIAL_ONE_TO_ONE_DATA), null, { root: true });

    const oneToOneTargetStudentId = data?.studentId || "";
    const isEnteringOneToOne = !!data?.enable;

    if (!oneToOneTargetStudentId && isEnteringOneToOne) return;

    if (student.id === state.idHelperOne) {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID, false), {
        id: isEnteringOneToOne ? oneToOneTargetStudentId : "",
        withTeacher: true,
      });
      return;
    }

    const isMe = student.id === oneToOneTargetStudentId;
    if (isEnteringOneToOne) {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID, false), { id: oneToOneTargetStudentId, withTeacher: true });
      if (isMe) {
        await state.manager?.switchChannels();
      }
      return;
    }

    if (isMe) {
      try {
        const { data: sessionData } = await RemoteTeachingService.getMinimalSessionDto(
          rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_SESSION_ID)],
          student.id,
        );

        // Set flag to prevent auto-clearing of targets during state restoration
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), true, { root: true });

        await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.GET_TEAMS), null, { root: true });
        commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_TEAM_MODE), sessionData.isTeamMode, { root: true });
        commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID, false), { id: "", withTeacher: true });
        await state.manager?.switchChannels();
        commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), sessionData.ratio, { root: true });
        commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), sessionData.ratioScrollPdf, { root: true });
        if (sessionData.position)
          commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), { x: sessionData.position.x, y: sessionData.position.y }, { root: true });
        commit(
          vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE),
          {
            id: sessionData.selectedExposureId,
            preventSelectFirstSlideAutomatically: !!sessionData.selectedExposureItemId,
          },
          { root: true },
        );
        if (sessionData.selectedExposureItemId)
          commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA), { id: sessionData.selectedExposureItemId }, { root: true });
        commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_PALETTE, false), {
          studentId: oneToOneTargetStudentId,
          isPaletteEnabled: sessionData.isPalette,
        });
        commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_CLASS_ACTION, false), {
          action: ClassActionFromValue(sessionData.lessonAction),
        });
        commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_WHITEBOARD, false), sessionData.isShowWhiteBoard);
        if (
          !isTeacherUseOnlySlideVisible({
            isTeacherUseOnly: !!rootGetters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)]?.teacherUseOnly,
            isWhiteboardVisible: sessionData.isShowWhiteBoard,
          })
        ) {
          commit(
            vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_OBJECTS),
            {
              objects: mapObjectsFromBE(sessionData.drawing?.objects || []),
            },
            { root: true },
          );
        }
        await dispatch(vuexName(MODULES.LESSON.DISPATCHES.PROCESS_VISIBLE_TARGETS_DATA_FROM_API), sessionData.drawing?.targets, {
          root: true,
        });
        if (sessionData.independentMode) {
          commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Independent, { root: true });
        } else {
          commit(vuexName(MODULES.WHITE_BOARD.COMMITS.SET_SHOULD_FETCH_MEDIA_STATE), true, { root: true });
          commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Normal, { root: true });
        }

        // Reset flag after restoration is complete
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), false, { root: true });
      } catch (err) {
        Logger.log(err);
        // Reset flag in case of error
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), false, { root: true });
      }
      return;
    }

    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID, false), { id: "", withTeacher: true });
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.UPDATE_MEDIA_STATE),
    async (data: TeacherUpdateMediaStateEventData) => {
      const { startMediaAt, duration, pauseMediaAt } = data;
      commit(vuexName(MODULES.WHITE_BOARD.COMMITS.SET_MEDIA_STATE), { startMediaAt, duration, pauseMediaAt }, { root: true });
    },
  );
  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.UPDATE_EXPOSURE), async (data: TeacherUpdateExposureEventData) => {
    const previousTimestamp = timestamps.onTeacherUpdateExposureTimestamp;
    if (previousTimestamp && data.timestamp < previousTimestamp) return;
    timestamps.onTeacherUpdateExposureTimestamp = data.timestamp;
    //set current exposure
    if (data.exposureId) {
      commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE), { id: data.exposureId }, { root: true });
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_WHITEBOARD, false), false);
    }
    //set current exposure item content
    commit(vuexName(MODULES.WHITE_BOARD.COMMITS.SET_SHOULD_FETCH_MEDIA_STATE), false, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA), { id: data.exposureItemId }, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), 0, { root: true });
    commit(vuexName(MODULES.WHITE_BOARD.COMMITS.RESET_STATE), null, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), { x: 0, y: 0 }, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), MIN_ZOOM_RATIO, { root: true });
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_WHITEBOARD, false), false);
    commit(vuexName(MODULES.LESSON.COMMITS.SET_VISIBLE_TARGET_TAGS), [], { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.REMOVE_HELPER), async () => {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_HELPER_INFO, false), undefined);
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.END_EXPOSURE), async (data: TeacherEndExposureEventData) => {
    commit(vuexName(MODULES.LESSON.COMMITS.SET_EXPOSURE_STATUS), { id: data.exposureId, status: ExposureStatus.COMPLETED }, { root: true });
    if (data.needUpdateRemainingTime) {
      const playedTime = calculatePlayedTime(rootGetters[vuexName(MODULES.LESSON.GETTERS.GET_EXPOSURES)]);
      commit(vuexName(MODULES.LESSON.COMMITS.SET_PLAYED_TIME), { time: playedTime }, { root: true });
    }
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.CLEAR_RAISING_HAND),
    async ({ studentId }: TeacherClearRaisingHandEventData) => {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_RAISING_HAND, false), {
        studentId: studentId,
        raisingHand: false,
      });
    },
  );

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.TOGGLE_INDEPENDENT_MODE),
    async (data: ToggleIndependentModeEventData) => {
      const firstItemId = data?.firstItemId ?? null;
      if (firstItemId) {
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Independent, { root: true });
        commit(vuexName(MODULES.STUDENT_TEACHING.COMMITS.SET_CURRENT_INDEPENDENT_ITEM_ID), firstItemId, { root: true });
      } else {
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Normal, { root: true });
        commit(vuexName(MODULES.STUDENT_TEACHING.COMMITS.SET_CURRENT_INDEPENDENT_ITEM_ID), null, { root: true });
      }
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.TOGGLE_HELPER_VIDEO), async (data: ToggleHelperVideoEventData) => {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_HELPER_VIDEO_STATUS, false), !!data?.show);
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SET_WHITEBOARD), async (isShowWhiteBoard: boolean) => {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_WHITEBOARD, false), isShowWhiteBoard);
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.UPDATE_CANVAS_OBJECT),
    async (data: TeacherUpdateCanvasObjectEventData) => {
      commit(vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_NEXT_OBJECT_TOBE_UPDATED), data, { root: true });
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SET_LESSON_ACTION), async (data: LessonActionEventData) => {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_CLASS_ACTION, false), {
      action: ClassActionFromValue(data.action),
    });
  });

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SET_ANNOTATION_MODE),
    async (data: TeacherUpdateAnnotationModeEventData) => {
      commit(vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_ANNOTATION_MODE), data.mode, { root: true });
    },
  );

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SCROLL_PDF), async (data: TeacherScrollPdfEventData) => {
    commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), data, { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.ZOOM_SLIDE), async (data: TeacherZoomSlideEventData) => {
    commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), data.ratio, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), { x: data.position.x, y: data.position.y }, { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.MOVE_ZOOMED_SLIDE), async (data: TeacherMoveZoomedSlideEventData) => {
    const { x, y } = data;
    commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), { x, y }, { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.SET_TEACHING_MODE), async () => {
    // do nothing
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEACHER, TeacherAction.END_SESSION), async () => {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_IS_SESSION_ENDED, false), true);
    await dispatch(vuexName(MODULES.STUDENT_ROOM.DISPATCHES.LEAVE_ROOM, false), { leave: true });
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_API_STATUS, false), {
      code: GLErrorCode.CLASS_HAS_BEEN_ENDED,
      message: fmtMsg(CommonLocale.CommonEndSession),
    });
    Modal.destroyAll();
  });

  // #endregion

  // #region WHITEBOARD MODULE HANDLERS

  handlerMap.set(
    createRealtimeHandlerKey(REALTIME_EVENT.WHITEBOARD, WhiteboardAction.TOGGLE_TARGET),
    async (data: ToggleTargetEventData, sender: string) => {
      if (sender === myStudentId) return;

      await dispatch(vuexName(MODULES.LESSON.DISPATCHES.PROCESS_VISIBLE_TARGET_BY_FROM_MESSAGE), data, { root: true });
    },
  );

  // #endregion

  // #region Helper MODULE HANDLERS
  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.SET_ONE_ONE), async (data: OneToOneRealtimeEventData) => {
    const { student, idOne } = state;
    if (!student) return;

    commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_INITIAL_ONE_TO_ONE_DATA), null, { root: true });

    const isEnteringOneToOne = !!data?.enable;
    const oneToOneTargetStudentId = data?.studentId || "";

    if (student.id === idOne) {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID, false), {
        id: isEnteringOneToOne ? oneToOneTargetStudentId : "",
        withTeacher: false,
      });
      if (!isEnteringOneToOne) {
        await state.manager?.switchChannels();
      }
      return;
    }

    const isMe = oneToOneTargetStudentId === student.id;

    if (isEnteringOneToOne) {
      commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID, false), { id: oneToOneTargetStudentId, withTeacher: false });
      if (isMe) {
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE), true, { root: true });
        await state.manager?.switchChannels();
      }
      return;
    }

    if (isMe) {
      try {
        const sessionId = rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_SESSION_ID)];
        if (!sessionId) return;
        const { data: sessionData } = await RemoteTeachingService.getMinimalSessionDto(sessionId, student.id);

        // Set flag to prevent auto-clearing of targets during state restoration
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), true, { root: true });

        await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.GET_TEAMS), null, { root: true });
        commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_TEAM_MODE), sessionData.isTeamMode, { root: true });
        commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID, false), { id: "", withTeacher: false });
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE), false, { root: true });
        commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), sessionData.ratio, { root: true });
        commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), sessionData.ratioScrollPdf, { root: true });
        if (sessionData.position)
          commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), { x: sessionData.position.x, y: sessionData.position.y }, { root: true });
        commit(
          vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE),
          {
            id: sessionData.selectedExposureId,
            preventSelectFirstSlideAutomatically: true,
          },
          { root: true },
        );
        commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA), { id: sessionData.selectedExposureItemId ?? "" }, { root: true });
        commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_PALETTE, false), {
          studentId: oneToOneTargetStudentId,
          isPaletteEnabled: sessionData.isPalette,
        });
        commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_CLASS_ACTION, false), {
          action: ClassActionFromValue(sessionData.lessonAction),
        });
        commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_WHITEBOARD, false), sessionData.isShowWhiteBoard);
        if (
          !isTeacherUseOnlySlideVisible({
            isTeacherUseOnly: !!rootGetters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)]?.teacherUseOnly,
            isWhiteboardVisible: sessionData.isShowWhiteBoard,
          })
        ) {
          commit(
            vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_OBJECTS),
            {
              objects: mapObjectsFromBE(sessionData.drawing?.objects || []),
            },
            { root: true },
          );
        }
        await dispatch(vuexName(MODULES.LESSON.DISPATCHES.PROCESS_VISIBLE_TARGETS_DATA_FROM_API), sessionData.drawing?.targets, {
          root: true,
        });
        commit(vuexName(MODULES.LESSON.COMMITS.SET_PLAYED_TIME), { time: sessionData.playedTime }, { root: true });
        commit(vuexName(MODULES.WHITE_BOARD.COMMITS.SET_SHOULD_FETCH_MEDIA_STATE), true, { root: true });
        if (sessionData.independentMode) {
          commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Independent, { root: true });
        } else {
          commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Normal, { root: true });
        }
        await state.manager?.switchChannels();

        // Reset flag after restoration is complete
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), false, { root: true });
      } catch (err) {
        Logger.log(err);
        // Reset flag in case of error
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_RESTORING_SESSION_STATE), false, { root: true });
      }
      return;
    }

    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_STUDENT_ONE_ID, false), { id: "", withTeacher: false });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.FAILED_SET_ONE_ONE), async () => undefined);

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.REQUEST_TO_JOIN_SESSION), async () => {
    // do nothing
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.CANCEL_REQUEST_TO_JOIN_SESSION), async () => {
    // do nothing
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.LEAVE_SESSION), async () => {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_HELPER_INFO, false), undefined);
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.TOGGLE_TEACHER_AUDIO), async (data: { isIgnored: string }) => {
    const { student, idHelperOne } = state;
    if (student?.id === idHelperOne) {
      commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE), data.isIgnored, { root: true });
    }
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.ENTER_CLASS), async (data: HelperEnterClassEventData) => {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_HELPER_INFO, false), data);
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.HELPER, HelperAction.BECOME_TEACHER), async () => {
    commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.MAKE_HELPER_THE_NEW_TEACHER, false), {});
  });

  // #endregion

  // #region TEAM MODULE HANDLERS

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEAM, TeamAction.TOGGLE_TEAM_MODE), async (data: ToggleTeamModeEventData) => {
    commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_TEAM_MODE), !!data?.isTeamMode, { root: true });
  });

  handlerMap.set(createRealtimeHandlerKey(REALTIME_EVENT.TEAM, TeamAction.UPSERT_TEAM), async () => {
    await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.GET_TEAMS), null, { root: true });
  });

  // #endregion
  return handlerMap;
};
